import styled, { css } from 'styled-components';
import { Link } from 'react-router-dom';
import { softSurface } from '../../components/brand/surfaces';
import { arrowUpRightMask } from '../../components/ui/icons/ArrowUpRight';

export const colors = {
  primaryBg: 'var(--paper)',
  primaryText: 'var(--ink)',
  secondaryText: 'var(--muted)',
  accent: 'var(--ink)',
  accentLight: 'var(--ink)',
  highlight: 'var(--ink)',
  borderColor: 'var(--line)',
  cardBg: 'var(--surface)',
  cardBgStrong: 'var(--surface-strong)',
  cardHover: 'var(--surface-strong)',
  ctaBg: 'var(--surface)',
};
export const breakpoints = {
  mobile: '(max-width: 480px)',
  tablet: '(max-width: 768px)',
};
const body = css`
  font-size: 1.6rem;
  line-height: 1.75;
  color: var(--muted);
`;
const card = css`
  ${softSurface}
`;
const button = css`
  display: inline-flex;
  align-items: center;
  gap: 12px;
  padding: 13px 20px;
  border: 1px solid var(--ink);
  border-radius: 999px;
  color: var(--ink);
  text-decoration: none;
  font-size: 1.4rem;
  line-height: 1.5;
  transition: background 0.2s;
  &:hover {
    background: var(--surface-strong);
  }
`;
export const Layout = styled.div`
  width: 100%;
  min-height: 100svh;
  background: var(--paper);
  color: var(--ink);
`;
export const HeroSection = styled.section`
  max-width: var(--page-width);
  padding: 160px var(--gutter) 64px;
  margin: 0 auto;
  @media (max-width: 760px) {
    padding-top: 130px;
    padding-bottom: 40px;
  }
`;
export const HeroTitle = styled.h1`
  font-size: clamp(4.4rem, 7.2vw, 10.4rem);
  max-width: 1080px;
  line-height: 1.04;
  margin: 0 0 28px;
  font-weight: 600;
  letter-spacing: -0.065em;
  white-space: pre-line;
  text-wrap: balance;
  overflow-wrap: break-word;
`;
export const ServicesHeroTitle = styled.h1`
  margin: 0 0 34px;
  font-weight: 600;
  strong {
    display: block;
    font-size: clamp(6.6rem, 11.5vw, 16rem);
    line-height: 0.95;
    letter-spacing: -0.075em;
    text-transform: uppercase;
    font-weight: 650;
  }
  span {
    display: block;
    margin-top: 24px;
    max-width: 920px;
    font-size: clamp(3.2rem, 4.8vw, 6.5rem);
    line-height: 1.08;
    letter-spacing: -0.045em;
    text-wrap: balance;
  }
  @media (max-width: 760px) {
    strong {
      font-size: clamp(5.3rem, 12.3vw, 9rem);
    }
    span {
      max-width: 550px;
      margin-top: 20px;
    }
  }
`;
export const HeroSubtitle = styled.p`
  ${body} font-size: 1.8rem;
  max-width: 740px;
  margin: 0;
`;
export const PageContainer = styled.div`
  width: calc(100% - 2 * var(--gutter));
  max-width: 1248px;
  margin: 0 auto 80px;
  border-top: 1px solid var(--line);
  padding-top: 36px;
`;
export const Section = styled.section`
  padding: 44px 0;
  &:not(:first-of-type) {
    border-top: 1px solid var(--line);
  }
  @media (max-width: 760px) {
    padding: 32px 0;
  }
`;
export const SectionTitle = styled.h2`
  font-size: clamp(2.8rem, 3.6vw, 5rem);
  margin: 0 0 20px;
  max-width: 800px;
`;
export const SectionText = styled.p`
  ${body} max-width: 850px;
  margin: 0 0 24px;
`;
export const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 26px;
  margin-top: 32px;
  counter-reset: services;
  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }
`;
export const ServiceCard = styled.article`
  ${card} padding: clamp(24px, 3vw, 42px);
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-height: 380px;
  position: relative;
  counter-increment: services;
  &::before {
    content: '0' counter(services);
    color: var(--brass);
    font: 400 4.6rem/1 var(--font-mono);
    letter-spacing: -0.08em;
    margin-bottom: 28px;
  }
  transition: box-shadow 0.3s;
  &:hover {
    box-shadow:
      inset 0 1px 0 #fff,
      0 16px 40px -24px #202b3526;
  }
`;
export const CardTitle = styled.h3`
  font-size: clamp(2.4rem, 2.6vw, 3.4rem);
  margin: 0;
`;
export const CardTagline = styled.p`
  ${body} color: var(--ink);
  margin: 0;
`;
export const CardDescription = styled.p`
  ${body} margin: 0 0 12px;
`;
export const CardLink = styled(Link)`
  font-size: 1.4rem;
  color: var(--ink);
  margin-top: auto;
  text-decoration: none;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding-top: 18px;
  border-top: 1px solid var(--line);
  &::after {
    ${arrowUpRightMask}
    font-size: 2rem;
  }
  &:hover {
    text-decoration: underline;
  }
`;
export const CtaSection = styled.div`
  ${card} padding: clamp(24px, 5vw, 70px);
  margin: 32px 0 56px;
`;
export const CtaTitle = styled.h3`
  font-size: clamp(3rem, 4vw, 5.6rem);
  margin: 0 0 20px;
  max-width: 780px;
`;
export const CtaText = styled.p`
  ${body} max-width: 760px;
  margin: 0;
`;
export const ButtonRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  margin-top: 30px;
`;
export const PrimaryButton = styled(Link)`
  ${button} background: var(--paper);
`;
export const SecondaryButton = styled(Link)`
  ${button} border-color: var(--line);
`;
export const FaqSection = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 36px;
  margin-top: 32px;
  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }
`;
export const FaqItem = styled.div`
  padding-top: 24px;
  border-top: 1px solid var(--line);
`;
export const FaqQuestion = styled.h4`
  font-size: 2rem;
  letter-spacing: -0.025em;
  margin: 0 0 14px;
`;
export const FaqAnswer = styled.p`
  ${body} margin: 0;
`;
export const BreadcrumbLink = styled(Link)`
  font-size: 1.3rem;
  text-decoration: none;
  display: inline-block;
  color: var(--muted);
  &::before {
    content: '← ';
  }
  &:hover {
    color: var(--ink);
  }
`;
export const List = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: grid;
  gap: 14px;
`;
export const ListItem = styled.li`
  ${body} padding-left: 20px;
  position: relative;
  &::before {
    content: '•';
    position: absolute;
    left: 0;
  }
`;
export const ProcessList = styled.ol`
  list-style: none;
  padding: 0;
  margin: 32px 0 0;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(220px, 100%), 1fr));
  gap: 24px;
  counter-reset: process;
`;
export const ProcessStep = styled.li`
  ${body} padding: 24px 0;
  border-top: 1px solid var(--line);
  counter-increment: process;
  &::before {
    content: '0' counter(process);
    display: block;
    color: var(--ink);
    font-size: 1.2rem;
    margin-bottom: 20px;
  }
`;
export const ShowcaseGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(260px, 100%), 1fr));
  gap: 18px;
`;
export const ShowcaseCard = styled(Link)`
  ${card} padding: 30px;
  color: var(--ink);
  text-decoration: none;
  &:hover {
    border-color: var(--ink);
  }
  &::after {
    ${arrowUpRightMask}
    font-size: 2.4rem;
    display: block;
    margin-top: 24px;
  }
`;
export const ShowcaseTitle = styled.h4`
  font-size: 2.2rem;
  margin: 0 0 12px;
`;
export const ShowcaseText = styled.p`
  ${body} margin: 0;
`;
export const Callout = styled.div`
  ${card} padding: 32px;
  margin: 24px 0;
`;
export const CalloutTitle = styled.h4`
  font-size: 2.4rem;
  margin: 0 0 18px;
`;
export const CalloutText = styled.p`
  ${body}
`;
export const CalloutLink = styled(Link)`
  font-size: 1.4rem;
  color: var(--ink);
`;
export const TestimonialCard = styled.div`
  border-left: 1px solid var(--ink);
  padding: 12px 0 12px 32px;
`;
export const TestimonialQuote = styled.p`
  font-size: clamp(2rem, 2.8vw, 3.6rem);
  line-height: 1.4;
  letter-spacing: -0.025em;
  margin: 0 0 24px;
`;
export const TestimonialSource = styled.p`
  font-size: 1.4rem;
  color: var(--muted);
  margin: 0;
`;
